import React, { useState, useEffect, useMemo, useRef } from 'react';

import { createPatient, updatePatientDemographics } from '../../services/api/visitApi';
import { linkABHAToPatient } from '../../services/api/abhaApi';
import { abhaAutofillPatch, isLastNameRequired, isLastNameLocked, isFirstNameLocked, isMaskedAbhaNumber } from '../../services/api/abhaProfile';
import { resolvePatientIdentity } from '../visit/patientIdentity';
import useAbhaDuplicateGuard, { ABHA_ALREADY_LINKED_CODE, ABHA_ALREADY_LINKED_NOTE } from '../../hooks/useAbhaDuplicateGuard';
import AbhaAlreadyLinkedNotice from '../abha/AbhaAlreadyLinkedNotice';
import { RELATIONS } from '../visit/RegisterVisitForm';
import ConsentBlock, { resolveConsentGranted } from '../common/ConsentBlock';
import AgeInput from '../common/AgeInput';
import AddressBlock from '../common/AddressBlock';
import { ADDRESS_FIELDS, PINCODE_ERROR, abhaAddressFill, abhaAddressValues, needsPostLinkAddressPatch } from '../../utils/patientAddress';
import { abhaReadyLabel } from '../../utils/scanShare';

const PINCODE_RE = /^[0-9]{6}$/;
const EMPTY_ADDRESS = Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, '']));
const addressOf = (form) => Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, form[f]]));

/**
 * InlineRegisterPatientDialog (C1 FE-6, S1-AC1) — inline "Register new
 * patient" for the admit desk, shown when a mobile search returns NO match.
 * Reuses the SAME field set + relation vocabulary as the canonical OPD
 * `RegisterVisitForm` (first_name/last_name/relation/age/dob/gender) so the
 * demographic capture doesn't drift into a parallel convention.
 *
 * IMPORTANT (see visitApi.createPatient doc): this registers the PATIENT ONLY
 * — it does NOT create an OPD visit. `RegisterVisitForm`/`registerVisit` were
 * NOT reused directly here because that path ALWAYS creates a visit (mandates
 * a doctor/nurse/lab-tech), which would spawn a junk OPD visit on every admit
 * — unacceptable at an admit desk (walk-in/emergency is the norm). The
 * `createPatient` call binds to an ASSUMED backend contract (documented in
 * visitApi.js) that does not exist on master yet — flagged for the backend at
 * integration.
 *
 * On success: the new patient is returned to the caller (`onRegistered`) and
 * the dialog closes — the admit form continues on the SAME page (no
 * navigation), preserving the ≤60s goal.
 *
 * CONSENT-CAPTURE P1 (D13) — this dialog always creates a BRAND NEW patient
 * (no prior consent record can exist), so the box pre-ticks from the server
 * catalogue default with no "on file" chip and no legacy banner — PROVIDED
 * the catalogue actually loaded. `consentCatalogEntry` is passed down by the
 * caller (AdmitPatientForm, which already fetched it) rather than re-fetched
 * here — one GET per admit-desk session, not one per dialog open.
 *
 * D15.1 — if the catalogue is unavailable, `resolveConsentGranted` returns
 * `false` (never fabricate) and submit stays disabled with a visible reason
 * until it loads or the desk retries.
 */
export default function InlineRegisterPatientDialog({ mobile, onRegistered, onCancel, onAbhaRemoved, consentCatalogEntry, initialAbhaSnapshot = null }) {
  const [form, setForm] = useState(() => {
    const base = {
      mobile: mobile || '',
      first_name: '',
      last_name: '',
      relation: 'SELF',
      age: '',
      dob: '',
      gender: '',
      ...EMPTY_ADDRESS,
    };
    // OR-121: an OTP ABHA address REPLACES the address fields; OR-137: a Scan & Share one fills
    // only an empty address (the dialog opens empty, so both fill here and nothing typed is lost).
    return { ...base, ...abhaAutofillPatch(initialAbhaSnapshot, base), ...abhaAddressFill(initialAbhaSnapshot, base) };
  });
  // R43: optional ABHA verified/created for the NEW patient; linked right after the patient is created.
  const [abhaResult, setAbhaResult] = useState(initialAbhaSnapshot);
  // What the server will write on link (null = ABHA has no address); the block marks ABHA vs Edited against it.
  const abhaValues = useMemo(() => abhaAddressValues(abhaResult), [abhaResult]);
  const abhaDup = useAbhaDuplicateGuard();
  const handleOpenDuplicatePatient = () => abhaDup.open(onRegistered);
  const [consentGranted, setConsentGranted] = useState(() => resolveConsentGranted(null, consentCatalogEntry || null));
  // The catalogue can arrive AFTER this dialog mounts. Seeding only in the
  // useState initialiser left the box permanently unticked in that case, which
  // silently breaks D6's pre-tick and (under D13) forces a manual tick before
  // the patient can be registered at all. Re-seed when it lands — but never
  // over a choice the operator has already made (QA, late-catalogue race).
  const consentTouched = useRef(false);
  useEffect(() => {
    if (consentTouched.current) return;
    setConsentGranted(resolveConsentGranted(null, consentCatalogEntry || null));
  }, [consentCatalogEntry]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // D15.1 — a failed/unavailable catalogue blocks submit too (never
  // fabricate consent to text the card couldn't render).
  // Gender is MANDATORY here (owner, admission flow). This dialog only ever
  // creates a NEW patient, so requiring it cannot block admitting someone who
  // already exists without a gender on record — that distinction is why it is
  // safe to enforce at this surface and not, say, on an edit path.
  const needGender = !form.gender;
  const lastNameRequired = isLastNameRequired(abhaResult);   // R139: optional only for an attached one-word ABHA name
  // The block does not report pincode validity: only a 6-digit pincode may be saved (empty is fine).
  const pincodeInvalid = !!form.address_pincode.trim() && !PINCODE_RE.test(form.address_pincode.trim());
  const unmet =
    form.mobile.length !== 10 || !form.first_name.trim() || (!form.last_name.trim() && lastNameRequired) || needGender || pincodeInvalid;
  // PO validation-timing ruling — same model as AdmitPatientForm: missing
  // fields no longer disable the button (consent is its only disabler), so
  // there is something to "attempt" and red can be earned rather than shown
  // on arrival. The guard below still refuses to submit anything incomplete.
  const [attempted, setAttempted] = useState(false);
  const showFieldErrors = attempted;
  const canSubmit = consentGranted && !!consentCatalogEntry && !busy;

  const submit = async () => {
    if (!canSubmit) return;            // consent-blocked: its reason line says why
    if (unmet) { setAttempted(true); return; }
    setBusy(true);
    setError(null);
    try {
      // R22 one-ABHA-one-patient: never create a second record for an ABHA that already belongs to one.
      const identity = resolvePatientIdentity(abhaResult);
      const linkable = identity?.abhaNumber && !isMaskedAbhaNumber(identity.abhaNumber);
      if (linkable && await abhaDup.check(identity.abhaNumber)) { setBusy(false); return; }
      abhaDup.clear();
      const patient = await createPatient({ ...form, consentGranted, abhaNumber: linkable ? identity.abhaNumber : undefined, scanId: abhaResult?.scanId || undefined });
      let abhaNote = '';
      if (linkable) {
        let linked = false;
        try {
          await linkABHAToPatient({
            patientId: patient.patient_id,
            abhaNumber: identity.abhaNumber,
            abhaAddress: identity.abhaAddress || '',
            recordVersion: 0,
            profile: identity.profile,
          });
          patient.abha_status = 'VERIFIED';
          linked = true;
        } catch (e) {
          // Patient exists now: hand it back with the reason instead of swallowing the failure.
          abhaNote = (e?.linkCode || e?.abhaCode) === ABHA_ALREADY_LINKED_CODE
            ? ABHA_ALREADY_LINKED_NOTE
            : `ABHA could not be linked: ${e?.message || 'please try again from the patient.'}`;
        }
        // D4: an OTP link just replaced the typed address with ABHA's. If staff had edited it, put
        // their six values back with ONE PATCH (the server marks those fields Edited).
        // OR-137: never after a Scan & Share snapshot (the claim keeps a saved address).
        if (linked && needsPostLinkAddressPatch(form, abhaResult)) {
          try {
            await updatePatientDemographics(patient.patient_id, addressOf(form));
          } catch (e) {
            abhaNote = `Address changes could not be saved: ${e?.message || 'please edit the address from the patient.'}`;
          }
        }
      }
      onRegistered(patient, abhaNote);
    } catch (e) {
      // R88: server refused a second patient for an already-linked ABHA — show the notice instead.
      if (await abhaDup.fromError(e)) { setBusy(false); return; }
      setError(e.message || 'Failed to register patient.');
    }
    setBusy(false);
  };

  const inputCls =
    'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 ' +
    'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm ' +
    'focus:outline-none focus:ring-2 focus:ring-teal-500';
  const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
  // Same field-level error convention as AdmitPatientForm — red border, red
  // focus ring (never falling back to teal on focus, which would make an
  // invalid field look fixed the moment you click it).
  const inputErrCls =
    'w-full px-2.5 py-2 rounded-lg border-2 border-red-400 dark:border-red-600 ' +
    'bg-red-50 dark:bg-red-900/20 text-slate-800 dark:text-slate-100 text-sm ' +
    'focus:outline-none focus:ring-2 focus:ring-red-500';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="register-patient-dialog"
    >
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Register new patient</h3>
          <button
            type="button"
            onClick={() => { abhaDup.clear(); onCancel(); }}
            className="ml-auto text-slate-500 dark:text-slate-400"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div
              className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5"
              data-testid="register-patient-error"
            >
              {error}
            </div>
          )}
          <div>
            <label className={labelCls} htmlFor="rp-mobile">Mobile *</label>
            <input
              id="rp-mobile"
              className={inputCls}
              inputMode="tel"
              value={form.mobile}
              onChange={set('mobile')}
              aria-label="Mobile"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls} htmlFor="rp-first">First name *</label>
              <input id="rp-first" className={!form.first_name.trim() && showFieldErrors ? inputErrCls : inputCls} aria-invalid={(!form.first_name.trim() && showFieldErrors) || undefined} data-testid="rp-first" value={form.first_name} onChange={set('first_name')} readOnly={isFirstNameLocked(abhaResult)} aria-label="First name" />
            </div>
            <div>
              <label className={labelCls} htmlFor="rp-last">Last name{lastNameRequired ? ' *' : ''}</label>
              <input id="rp-last" className={!form.last_name.trim() && lastNameRequired && showFieldErrors ? inputErrCls : inputCls} aria-invalid={(!form.last_name.trim() && lastNameRequired && showFieldErrors) || undefined} data-testid="rp-last" value={form.last_name} onChange={set('last_name')} readOnly={isLastNameLocked(abhaResult)} aria-label="Last name" />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="rp-relation">Relation</label>
            <select id="rp-relation" className={inputCls} value={form.relation} onChange={set('relation')} aria-label="Relation">
              {RELATIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Age</label>
              {/* Reuses the SAME AgeInput as Register Visit (owner: "we cant
                  select nd days like we can do it in Register visit"), so a
                  neonate can be recorded in days/months here too and the
                  stored value format stays identical across both surfaces. */}
              <AgeInput
                value={form.age}
                onChange={(age) => setForm((f) => ({ ...f, age }))}
                inputClassName={inputCls}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="rp-dob">Date of birth</label>
              <input
                id="rp-dob"
                type="date"
                className={inputCls}
                data-testid="rp-dob"
                max={new Date().toLocaleDateString('en-CA')}
                value={form.dob}
                onChange={set('dob')}
                aria-label="Date of birth"
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="rp-gender">Gender *</label>
              <select
                id="rp-gender"
                className={needGender && showFieldErrors ? inputErrCls : inputCls}
                aria-invalid={(needGender && showFieldErrors) || undefined}
                data-testid="rp-gender"
                value={form.gender}
                onChange={set('gender')}
                aria-label="Gender"
              >
                <option value="">—</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>
          <div>
            <AddressBlock
              value={form}
              onChange={(next) => setForm((f) => ({ ...f, ...next }))}
              abhaValues={abhaValues}
              mode="collapsed"
              idPrefix="rp-address"
              testIdPrefix="rp-address"
            />
            {pincodeInvalid && showFieldErrors && (
              <div className="text-[11.5px] text-red-600 dark:text-red-400 mt-1" data-testid="rp-address-pincode-invalid">{PINCODE_ERROR}</div>
            )}
          </div>

          {abhaDup.duplicate && (
            <AbhaAlreadyLinkedNotice
              patientName={abhaDup.duplicate.patientName}
              uhid={abhaDup.duplicate.uhid}
              patientId={abhaDup.duplicate.patientId}
              onOpenPatient={handleOpenDuplicatePatient}
              opening={abhaDup.opening}
            />
          )}
          <div data-testid="rp-abha">
            {abhaResult && (
              <div className="flex items-center gap-2 text-[12.5px] text-slate-700 dark:text-slate-200">
                <span data-testid="rp-abha-verified">ABHA ready to link: {abhaReadyLabel(abhaResult)}</span>
                <button type="button" className="ml-auto text-teal-700 dark:text-teal-300 underline" onClick={() => { abhaDup.clear(); setAbhaResult(null); if (onAbhaRemoved) onAbhaRemoved(); }}>Remove</button>
              </div>
            )}
          </div>

          {/* CONSENT-CAPTURE P1 (D13) — always a brand-new patient here, so
              always pre-ticked, no "on file" chip. */}
          <ConsentBlock
            catalogEntry={consentCatalogEntry || null}
            checked={consentGranted}
            onChange={(v) => { consentTouched.current = true; setConsentGranted(v); }}
            existingEntry={null}
          />
        </div>

        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex flex-col items-end gap-1.5">
          <div className="flex justify-end gap-2 w-full">
            <button
              type="button"
              onClick={() => { abhaDup.clear(); onCancel(); }}
              className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60"
              data-testid="register-patient-confirm"
            >
              {busy ? 'Registering…' : 'Register patient'}
            </button>
          </div>
          {!consentCatalogEntry ? (
            <p className="text-[11px] text-slate-500 dark:text-slate-400" data-testid="consent-required-reason">
              Consent text unavailable — retry.
            </p>
          ) : !consentGranted && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400" data-testid="consent-required-reason">
              Consent is required to register this patient.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
